import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronRight, Tag, Tags } from "lucide-react";
import { getEquipmentIcon } from "@/components/equipment/IconPicker";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import {
  useEquipmentTagsTree,
  type EquipmentTagTreeNode,
} from "@/hooks/useEquipmentTags";
import { cn } from "@/lib/utils";

const TAG_ROUTE_PREFIX = "/equipment/tag/";

function findPath(
  nodes: EquipmentTagTreeNode[],
  tagId: string,
): string[] | null {
  for (const node of nodes) {
    if (node.id === tagId) return [node.id];
    const childPath = findPath(node.children, tagId);
    if (childPath) return [node.id, ...childPath];
  }
  return null;
}

interface TagNodeProps {
  node: EquipmentTagTreeNode;
  activeTagId: string | null;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onNavigate: () => void;
}

function TagNode({
  node,
  activeTagId,
  expanded,
  onToggle,
  onNavigate,
}: TagNodeProps) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const Icon = getEquipmentIcon(node.icon) ?? Tag;

  return (
    <SidebarMenuSubItem>
      <Collapsible open={isOpen}>
        <SidebarMenuSubButton
          asChild
          className={cn(
            "pr-1",
            activeTagId === node.id &&
              "bg-sidebar-accent text-primary font-medium",
          )}
        >
          <NavLink
            to={`${TAG_ROUTE_PREFIX}${node.id}`}
            onClick={onNavigate}
            title={node.fullPath}
            data-testid={`sidebar-equipment-tag-${node.id}`}
          >
            <Icon className="h-4 w-4" />
            <span className="flex-1 truncate">{node.name}</span>
            {hasChildren && (
              <ChevronRight
                aria-label={isOpen ? "Collapse" : "Expand"}
                data-testid={`sidebar-equipment-tag-toggle-${node.id}`}
                className={cn(
                  "h-4 w-4 shrink-0 transition-transform duration-200",
                  isOpen && "rotate-90",
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggle(node.id);
                }}
              />
            )}
          </NavLink>
        </SidebarMenuSubButton>
        {hasChildren && (
          <CollapsibleContent>
            <SidebarMenuSub className="mx-2 mr-0 px-1.5 pr-0">
              {node.children.map((child) => (
                <TagNode
                  key={child.id}
                  node={child}
                  activeTagId={activeTagId}
                  expanded={expanded}
                  onToggle={onToggle}
                  onNavigate={onNavigate}
                />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        )}
      </Collapsible>
    </SidebarMenuSubItem>
  );
}

/** Equipment tags as a nested, expandable tree in the left sidebar. */
export function EquipmentSidebarTags({
  onNavigate,
}: {
  onNavigate: () => void;
}) {
  const { data: tree = [] } = useEquipmentTagsTree();
  const location = useLocation();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const activeTagId = location.pathname.startsWith(TAG_ROUTE_PREFIX)
    ? location.pathname.slice(TAG_ROUTE_PREFIX.length)
    : null;

  // Reveal the active tag by expanding its ancestors
  const activePathKey = activeTagId
    ? (findPath(tree, activeTagId) ?? []).slice(0, -1).join(",")
    : "";
  useEffect(() => {
    if (!activePathKey) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const id of activePathKey.split(",")) next.add(id);
      return next;
    });
  }, [activePathKey]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      {tree.map((node) => (
        <TagNode
          key={node.id}
          node={node}
          activeTagId={activeTagId}
          expanded={expanded}
          onToggle={toggle}
          onNavigate={onNavigate}
        />
      ))}
      {/* Equipment without a tag can't be reached through the tree */}
      <SidebarMenuSubItem>
        <SidebarMenuSubButton
          asChild
          className={cn(
            "pr-1 text-muted-foreground",
            location.pathname === "/equipment/untagged" &&
              "bg-sidebar-accent text-primary font-medium",
          )}
        >
          <NavLink
            to="/equipment/untagged"
            onClick={onNavigate}
            data-testid="sidebar-equipment-untagged"
          >
            <Tags className="h-4 w-4" />
            <span className="flex-1 truncate">Untagged</span>
          </NavLink>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    </>
  );
}
